import React, {
  useMemo, useState, useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper';
import { Img } from 'react-image';
import { useSelector } from 'react-redux';
import { fromStore } from '../../selectors';
import { Loader, PizzaSizer } from '../../components';
import {
  getIdGood, getImageUrl, getWeightAndMeasure,
} from '../../utils';
import { AddCartButton } from './AddCartButton';
import { GoodPrice } from './GoodPrice';
import { FavotiteButton } from './FavotiteButton';
import { Slide } from './Slide';

export function GroupCard({ id }) {
  const navigate = useNavigate();
  const itemId = getIdGood(id);
  const item = useSelector(fromStore.goodsByIdSelector(itemId));
  const { items = [] } = item;
  const defaultOption = items.find((option) => option.isDefault) || items[0];
  const [activeOption, setActiveOption] = useState(defaultOption.isStop ? items.find((option) => !option.isStop) : defaultOption);
  const cartId = activeOption.id;
  const categories = useSelector(fromStore.denormalizedCategories);
  const weightData = getWeightAndMeasure(activeOption.weight, activeOption.measure);
  const weight = weightData.weight ? `${weightData.measure.title}: ${weightData.weight} ${weightData.measure.value}` : null;
  const weightdopData = getWeightAndMeasure(activeOption.weightdop, activeOption.measuredop);
  const weightdop = weightdopData.weight ? `${weightdopData.measure.title}: ${weightdopData.weight} ${weightdopData.measure.value}` : null;
  const onClickDetail = useCallback(() => {
    const newPath = categories.find((category) => category.id === item.category.id).key;
    navigate(`/menu/${newPath}/${itemId}`);
    window.ym(92424510, 'reachGoal', 'click-dish');

    window.dataLayer.push(
      {
        ecommerce: {
          currencyCode: 'RUB',
          detail: {
            products: {
              id: activeOption.id || itemId,
              name: activeOption.name || item.name,
              brand: 'Sushibox',
              category: item.category.title,
              price: activeOption.price || item.price,
            },
          },
        },
      },
    );
  }, [activeOption, activeOption.id]);

  const renderImage = useMemo(() => (
    <div className="text-center">
      <Img
        alt={item.name}
        src={getImageUrl(item.image)}
        className="mw-100 card-rounded"
        loader={<Loader image />}
      />
    </div>
  ), [item.image]);

  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const renderCarousel = useMemo(() => (
    <div onClick={stopPropagation} aria-hidden="true">
      <Swiper navigation modules={[Navigation, Pagination]}>
        <SwiperSlide>
          <Slide name={item.name} image={item.image} click={onClickDetail} />
        </SwiperSlide>
        <SwiperSlide>
          <Slide name={item.name} image={item.secondImage} click={onClickDetail} />
        </SwiperSlide>
      </Swiper>
    </div>
  ), [item.name, item.image, item.secondImage, onClickDetail, stopPropagation]);

  if (!items || items.length === 0) return null;

  return (
    <div key={id} onClick={onClickDetail} aria-hidden className="col-xl-4">
      <div className="card mb-xl-8 border-hover-dark cursor-pointer card-xl-stretch">
        <div className="card-header border-0 pt-5">
          <div className="d-flex flex-stack w-100 align-items-start">
            <h3 className="card-title align-items-start flex-column m-0">
              <span className="card-label fw-bolder text-dark">{activeOption.name}</span>
              <div className="d-flex align-items-center mt-2">
                {weight && <span className="badge badge-dark">{weight}</span>}
                {weightdop && <span className="badge badge-secondary ms-1">{weightdop}</span>}
                {activeOption.hot && <img title="Острое" className="ms-1 w-15px" src="/assets/media/pepper.png" alt="острое" />}
                {activeOption.vegan && <img title="Веган" className="ms-1 w-15px" src="/assets/media/vegan.png" alt="веган" />}
              </div>
            </h3>
            <FavotiteButton id={String(activeOption.id)} />
          </div>
        </div>
        <div className="card-body pt-5 d-flex flex-column justify-content-between h-100">
          {item.secondImage ? renderCarousel : renderImage}
          {items && items.filter((option) => !option.isStop).length > 1 && (
            <div className="text-center mt-4">
              <PizzaSizer
                items={items.filter((option) => !option.isStop).sort((a, b) => (b.order || 0) - (a.order || 0)).map((option) => ({ ...option, title: option.groupTitle }))}
                selected={activeOption}
                onChange={(value) => setActiveOption(value)}
              />
            </div>
          )}
          <div className="d-flex flex-stack mt-10">
            <GoodPrice price={activeOption.price} oldPrice={activeOption.oldPrice} id={String(activeOption.id)} />
            <AddCartButton isStop={activeOption.isStop} id={cartId} item={activeOption} />
          </div>
        </div>
      </div>
    </div>
  );
}

GroupCard.propTypes = {
  id: PropTypes.string.isRequired,
};
